import { createFileRoute } from '@tanstack/react-router'
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  Download,
  FileText,
  Home,
  XCircle,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { customerDetailService } from '@/features/customers/services/customerDetailService'
import {
  type Customer,
  customerService,
} from '@/features/customers/services/customerService'
import type { Invoice } from '@/features/customers/types/customerDetail.types'
import { PageTitle } from '@/shared/components/common/PageTitle'
import { Badge } from '@/shared/components/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent } from '@/shared/components/ui/card'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

export const Route = createFileRoute(
  '/_auth/customers/$customerId/invoices/$invoiceId'
)({
  component: InvoiceDetailPage,
})

function InvoiceDetailPage() {
  const { customerId, invoiceId } = Route.useParams()
  const { setBreadcrumbs } = useBreadcrumbs()
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      try {
        // Load customer
        const allCustomersResponse = await customerService.getAllCustomers()
        const allCustomers = ((
          allCustomersResponse as unknown as { data: Customer[] }
        )?.data || allCustomersResponse) as Customer[]
        const foundCustomer = allCustomers.find(
          (c: Customer) => c.id === customerId
        )
        if (foundCustomer) {
          setCustomer(foundCustomer)
        }

        // Load invoice
        const invoices = (await customerDetailService.getInvoices(
          customerId
        )) as Invoice[]
        const foundInvoice = invoices.find((i: Invoice) => i.id === invoiceId)
        if (foundInvoice) {
          setInvoice(foundInvoice)
        } else {
          setError('Invoice not found')
        }
      } catch (err) {
        setError('Failed to load invoice')
        toast.error(extractErrorMessage(err))
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [customerId, invoiceId])

  // Breadcrumbs setup
  const breadcrumbsContent = useMemo(() => {
    if (!customer || !invoice) return null

    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to='/customers'>Customers</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to={`/customers/${customerId}`}>
              {customer.name}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to={`/customers/${customerId}`}>
              Invoices
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{invoice.number}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }, [customer, invoice, customerId])

  useEffect(() => {
    if (breadcrumbsContent) {
      setBreadcrumbs(breadcrumbsContent)
    }
  }, [setBreadcrumbs, breadcrumbsContent])

  const getStatusVariant = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return 'default'
      case 'sent':
        return 'secondary'
      case 'draft':
        return 'outline'
      case 'void':
        return 'destructive'
      default:
        return 'outline'
    }
  }

  const getStatusIcon = (status: Invoice['status']) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className='w-4 h-4' />
      case 'sent':
        return <Clock className='w-4 h-4' />
      case 'draft':
        return <FileText className='w-4 h-4' />
      case 'void':
        return <XCircle className='w-4 h-4' />
      default:
        return <AlertCircle className='w-4 h-4' />
    }
  }

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-muted-foreground'>Loading invoice...</div>
      </div>
    )
  }

  if (error || !invoice || !customer) {
    return (
      <div className='text-center py-12'>
        <div className='text-red-600'>{error || 'Invoice not found'}</div>
      </div>
    )
  }

  return (
    <div className='min-h-screen'>
      <div className='max-w-[1000px] mx-auto p-6'>
        {/* Header */}
        <div className='flex items-center justify-between mb-8'>
          <PageTitle
            title='Invoice Details'
            subtitle={`Invoice ${invoice.number} for ${customer.name}`}
          />
          <div className='flex items-center gap-3'>
            {invoice.pdf_url && (
              <Button variant='outline' className='gap-2'>
                <Download className='w-4 h-4' />
                Download
              </Button>
            )}
          </div>
        </div>

        {/* Invoice Header Info */}
        <div className='grid grid-cols-1 md:grid-cols-4 gap-6 mb-8'>
          <Card className='bg-transparent border'>
            <CardContent className='p-4'>
              <div className='space-y-2'>
                <h3 className='text-sm font-medium text-muted-foreground'>
                  Invoice Number
                </h3>
                <p className='text-lg font-semibold'>{invoice.number}</p>
              </div>
            </CardContent>
          </Card>

          <Card className='bg-transparent border'>
            <CardContent className='p-4'>
              <div className='space-y-2'>
                <h3 className='text-sm font-medium text-muted-foreground'>
                  Date of Issue
                </h3>
                <div className='flex items-center gap-2'>
                  <Calendar className='w-4 h-4 text-muted-foreground' />
                  <p className='font-medium'>{formatDate(invoice.issued_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className='bg-transparent border'>
            <CardContent className='p-4'>
              <div className='space-y-2'>
                <h3 className='text-sm font-medium text-muted-foreground'>
                  Date Due
                </h3>
                <div className='flex items-center gap-2'>
                  <Calendar className='w-4 h-4 text-muted-foreground' />
                  <p className='font-medium'>{formatDate(invoice.due_date)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className='bg-transparent border'>
            <CardContent className='p-4'>
              <div className='space-y-2'>
                <h3 className='text-sm font-medium text-muted-foreground'>
                  Payment Status
                </h3>
                <Badge
                  variant={getStatusVariant(invoice.status)}
                  className='gap-1'
                >
                  {getStatusIcon(invoice.status)}
                  {invoice.status === 'paid' ? 'Paid' : 'Pending'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customer Details */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-8'>
          <Card className='bg-transparent border'>
            <CardContent className='p-6'>
              <h3 className='text-lg font-semibold mb-4'>From</h3>
              <div className='space-y-2'>
                <p className='font-medium'>Your Company</p>
                <p className='text-sm text-muted-foreground'>
                  company@example.com
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className='bg-transparent border'>
            <CardContent className='p-6'>
              <h3 className='text-lg font-semibold mb-4'>Bill to</h3>
              <div className='space-y-2'>
                <p className='font-medium'>{customer.name}</p>
                <p className='text-sm text-muted-foreground'>
                  {customer.email}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Order Details */}
        <Card className='bg-transparent border mb-8'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-6'>
              <h3 className='text-lg font-semibold'>Order Details</h3>
              <div className='flex items-center gap-2'>
                <span className='text-sm text-muted-foreground'>
                  Show Zero Charges
                </span>
                <input type='checkbox' className='rounded' />
              </div>
            </div>

            {/* Line Items Table */}
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead>
                  <tr className='border-b'>
                    <th className='text-left py-3 px-2 font-medium'>
                      Subscription
                    </th>
                    <th className='text-left py-3 px-2 font-medium'>
                      Description
                    </th>
                    <th className='text-left py-3 px-2 font-medium'>
                      Interval
                    </th>
                    <th className='text-right py-3 px-2 font-medium'>
                      Quantity
                    </th>
                    <th className='text-right py-3 px-2 font-medium'>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lines.map((line, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    <tr key={index} className='border-b'>
                      <td className='py-4 px-2 font-medium'>Pro</td>
                      <td className='py-4 px-2 text-muted-foreground'>
                        {line.description}
                      </td>
                      <td className='py-4 px-2 text-muted-foreground'>
                        {line.period_start && line.period_end
                          ? `${formatDate(line.period_start)} - ${formatDate(line.period_end)}`
                          : 'One-time'}
                      </td>
                      <td className='py-4 px-2 text-right'>{line.qty}</td>
                      <td className='py-4 px-2 text-right font-medium'>
                        {formatCurrency(line.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className='mt-8 space-y-4'>
              <div className='flex justify-between items-center text-right'>
                <span className='text-lg font-medium'>Subtotal</span>
                <span className='text-lg font-medium'>
                  {formatCurrency(invoice.amount)}
                </span>
              </div>

              <div className='flex justify-between items-center text-right'>
                <span className='text-lg font-medium'>Net payable</span>
                <span className='text-lg font-medium'>
                  {formatCurrency(invoice.total_amount)}
                </span>
              </div>

              <div className='flex justify-between items-center text-right'>
                <span className='text-lg font-medium'>Amount paid</span>
                <span className='text-lg font-medium'>
                  {formatCurrency(invoice.paid_at ? invoice.total_amount : 0)}
                </span>
              </div>

              <div className='flex justify-between items-center text-right border-t pt-4'>
                <span className='text-xl font-semibold'>Remaining balance</span>
                <span className='text-xl font-semibold'>
                  {formatCurrency(invoice.paid_at ? 0 : invoice.total_amount)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
